import { Button, Skeleton, Typography } from "@/components/atoms";
import { TextInput } from "@/components/atoms/TextInput";
import {
  FormPicker,
  type FormPickerOption,
} from "@/components/molecules/FormPicker";
import { OptionPickerModal } from "@/components/molecules/OptionPickerModal";
import { measureOptionPickerAnchor } from "@/components/molecules/optionPicker/measureOptionPickerAnchor";
import type { OptionPickerAnchorRect } from "@/components/molecules/optionPicker/OptionPickerModal.types";
import { FullScreenLoader } from "@/components/molecules/FullScreenLoader";
import { PhoneInputRow } from "@/components/molecules/PhoneInputRow";
import { ServiceRequestSuccessDialog } from "@/components/molecules/dialogs/ServiceRequestSuccessDialog";
import { Toast } from "@/components/molecules/Toast";
import { LogoHeader } from "@/components/molecules/header/LogoHeader";
import { apiClient } from "@/core/api/client";
import { Endpoints } from "@/core/api/endpoints";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useIsAdhaJourney, useTranslation, useUIColors } from "@/core/hooks";
import { logger } from "@/core/logger";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { ALMETLAA_ACTION } from "@/core/constants/dynatraceCatalog";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { trackDynatraceEvent } from "@/core/services/dynatraceService";
import { trackCatalogEvent } from "@/core/services/trackDynatraceCatalog";
import { useAppStore, type Property } from "@/core/stores/appStore";
import { COUNTRIES, type Country } from "@/core/utils/countryCodes";
import { isValidEmail, validatePhoneNumber } from "@/core/utils/validation";
import { isSmallScreen } from "@/core/utils/responsive";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocalSearchParams, useRouter } from "expo-router";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import * as ServiceRequestService from "../services/serviceRequestService";
import type { FlatTranslationKey } from "@/core/types/i18n";

export function ServiceRequestCreateScreen() {
  const { t } = useTranslation();
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const supportLabel = (i18nKey: FlatTranslationKey): string => t(i18nKey);
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const queryClient = useQueryClient();
  const params = useLocalSearchParams<{
    categoryId?: string;
    categoryName?: string;
    description?: string;
    unitId?: string;
    unitLabel?: string;
    authFlow?: string;
  }>();

  const properties = useAppStore((state) => state.properties);
  const user = useAppStore((state) => state.auth.user);
  const isAuthenticated = useAppStore((state) => state.auth.isAuthenticated);
  const isAdhaJourney = useIsAdhaJourney();
  useEffect(() => {
    trackDynatraceEvent({
      p_screen: "serviceRequest",
      p_actionName: "Open Service Request Screen",
      eventType: "view",
    });
  }, []);

  useCatalogDynatraceView(
    DT_SCREEN.ALMETLAA_SUPPORT,
    ALMETLAA_ACTION.SUPPORT.CREATE_VIEW,
    isAdhaJourney,
  );

  // ADHA users have no owned units — hide unit picker. Normal login (web + native) keeps it.
  const showUnitPicker = isAuthenticated && !isAdhaJourney;
  const displayName = user?.full_name || user?.firstnameEN || user?.name || "";
  const friendlyName = isAuthenticated
    ? displayName
    : supportLabel("support.guestNameFallback");
  const defaultCountry: Country =
    COUNTRIES.find((c) => c.code === "+971") || COUNTRIES[0]!;

  const { data: categories = [], isLoading: categoriesLoading } = useQuery<
    FormPickerOption[]
  >({
    queryKey: [
      "service-request-categories",
      isAuthenticated,
      params.authFlow,
      isAdhaJourney,
      user?.registration_status,
    ],
    queryFn: async () => {
      try {
        const endpoint =
          (Endpoints.support as any).serviceRequestCategories ||
          "/v1/customers/get-list-values";
        const res = await apiClient.get<any>(endpoint, {
          params: { listValues: "SERVICE_REQUEST_LIST" },
        });
        let rawList =
          res?.data?.data?.serviceRequestTypes ??
          res?.data?.serviceRequestTypes ??
          res?.data?.data?.serviceRequests ??
          res?.data?.serviceRequests ??
          [];

        const isGuestOrOtp = !isAuthenticated || params.authFlow === "true";
        if (!isGuestOrOtp) {
          if (isAdhaJourney) {
            rawList = rawList.filter(
              (item: any) => item?.isAvailableForADHACustomer === true
            );
          } else {
            // Non-ADHA flow (credential login)
            rawList = rawList.filter(
              (item: any) => item?.isAvailableForADHACustomer === false
            );
          }
        }

        const mapped: FormPickerOption[] = rawList
          .filter((item: any) => item?.Id && item?.Name)
          .map(
            (item: any, idx: number): FormPickerOption => ({
              id: item.Id,
              label: item.Name,
              key: `${item.Id}-${idx}`,
            }),
          )
          .filter(
            (item: any, idx: number, arr: any[]) =>
              arr.findIndex((x: any) => x.label === item.label) === idx,
          )
          .sort((a: FormPickerOption, b: FormPickerOption) =>
            a.label.localeCompare(b.label),
          );
        logger.info("ServiceRequestCreateScreen", "Categories mapped", {
          rawCount: Array.isArray(rawList) ? rawList.length : 0,
          count: mapped.length,
        });
        if (mapped.length) return mapped;
        // Fallback to preset list if API returns empty
        const preset = (ServiceRequestService as any)
          .SERVICE_REQUEST_TYPES_PRESET as any[];
        return (preset || []).map((item: any) => ({
          id: item.id,
          label: item.name,
          key: item.key,
        }));
      } catch (error: any) {
        logger.error(
          "ServiceRequestCreateScreen",
          "Failed to fetch categories",
          { error: error?.message || error },
        );
        // Fallback to preset list on error
        const preset = (ServiceRequestService as any)
          .SERVICE_REQUEST_TYPES_PRESET as any[];
        return (preset || []).map((item: any) => ({
          id: item.id,
          label: item.name,
          key: item.key,
        }));
      }
    },
    staleTime: 10 * 60 * 1000,
  });

  const unitOptions: FormPickerOption[] = useMemo(
    () =>
      (properties || []).map((unit: Property) => ({
        id: unit.id,
        label: [unit.unitNumber, unit.projectName].filter(Boolean).join(" "),
      })),
    [properties],
  );

  const categoryOptions: FormPickerOption[] = useMemo(
    () => categories,
    [categories],
  );

  const preselectedCategory = useMemo(() => {
    if (!categoryOptions.length) return undefined;
    if (params.categoryId) {
      const foundById = categoryOptions.find((c) => c.id === params.categoryId);
      if (foundById) return foundById;
    }
    if (params.categoryName) {
      const target = String(params.categoryName).toLowerCase();
      const foundByName = categoryOptions.find(
        (c) => c.label.toLowerCase() === target,
      );
      if (foundByName) return foundByName;
    }
    return undefined;
  }, [categoryOptions, params.categoryId, params.categoryName]);

  const [selectedUnit, setSelectedUnit] = useState<
    FormPickerOption | undefined
  >(
    params.unitId && params.unitLabel
      ? { id: params.unitId, label: params.unitLabel }
      : undefined,
  );
  const [selectedCategory, setSelectedCategory] = useState<
    FormPickerOption | undefined
  >(preselectedCategory);
  const [description, setDescription] = useState(params.description ?? "");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<Country>(
    () => defaultCountry,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [serviceRequestSuccessVisible, setServiceRequestSuccessVisible] =
    useState(false);

  const [unitPickerVisible, setUnitPickerVisible] = useState(false);
  const [categoryPickerVisible, setCategoryPickerVisible] = useState(false);
  const [unitPickerAnchor, setUnitPickerAnchor] =
    useState<OptionPickerAnchorRect | null>(null);
  const [categoryPickerAnchor, setCategoryPickerAnchor] =
    useState<OptionPickerAnchorRect | null>(null);
  const unitPickerTriggerRef = useRef<View>(null);
  const categoryPickerTriggerRef = useRef<View>(null);

  const [errors, setErrors] = useState<{
    unit?: string;
    category?: string;
    description?: string;
    email?: string;
    phone?: string;
  }>({});
  const [touched, setTouched] = useState<{
    unit: boolean;
    category: boolean;
    description: boolean;
    email: boolean;
    phone: boolean;
  }>({
    unit: false,
    category: false,
    description: false,
    email: false,
    phone: false,
  });

  const selectedProperty = useMemo(
    () =>
      properties.find(
        (unit) => unit.id === (selectedUnit?.id || params.unitId),
      ),
    [properties, selectedUnit?.id, params.unitId],
  );

  // initialize selected unit when units load (normal login only)
  useEffect(() => {
    if (!showUnitPicker) return;
    if (!selectedUnit && params.unitId) {
      const match = unitOptions.find((u) => u.id === params.unitId);
      if (match) {
        setSelectedUnit(match);
      }
    }
  }, [unitOptions, selectedUnit, params.unitId, showUnitPicker]);

  // apply preselected category when categories load
  useEffect(() => {
    if (!selectedCategory && preselectedCategory) {
      setSelectedCategory(preselectedCategory);
    }
  }, [preselectedCategory, selectedCategory]);

  useEffect(() => {
    if (params.description && !description) {
      setDescription(params.description);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.description]);

  useEffect(() => {
    // Prefill email/phone from user when available
    if (user?.primary_email && !email) {
      setEmail(user.primary_email);
    }
    if (user?.primary_phone && !phone) {
      setPhone(user.primary_phone);
    }
    if (user?.primary_country_code) {
      const found = COUNTRIES.find(
        (c) =>
          c.code === user.primary_country_code ||
          c.code.replace("+", "") === user.primary_country_code,
      );
      if (found) {
        setSelectedCountry(found);
      }
    }
  }, [user, email, phone]);

  const validateEmailValue = (value: string) => {
    if (!value.trim() || !isValidEmail(value.trim())) {
      return supportLabel("support.errorEmail");
    }
    return "";
  };

  const validatePhoneValue = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return "";
    const phoneError = validatePhoneNumber(trimmed);
    if (phoneError) return phoneError;
    return "";
  };

  const validateDescriptionValue = (value: string) => {
    if (!value.trim()) {
      return supportLabel("support.errorDescribeIssue");
    }
    return "";
  };

  const validate = () => {
    const nextErrors: typeof errors = {};

    // Normal login: unit required. ADHA: no owned units — skip.
    if (showUnitPicker && !selectedUnit) {
      nextErrors.unit = supportLabel("support.errorSelectUnit");
    }

    if (!selectedCategory) {
      nextErrors.category = supportLabel("support.errorSelectCategory");
    }

    const descriptionError = validateDescriptionValue(description);
    if (descriptionError) {
      nextErrors.description = descriptionError;
    }

    if (!isAuthenticated) {
      const emailError = validateEmailValue(email);
      if (emailError) {
        nextErrors.email = emailError;
      }

      if (params.authFlow !== 'true') {
        const phoneError = validatePhoneValue(phone);
        if (phoneError) {
          nextErrors.phone = phoneError;
        }
      }
    }

    setErrors(nextErrors);
    setTouched({
      unit: true,
      category: true,
      description: true,
      email: true,
      phone: true,
    });

    return Object.keys(nextErrors).length === 0;
  };

  const isSubmitDisabled = useMemo(() => {
    if (isSubmitting || categoriesLoading) return true;
    if (!selectedCategory || !!validateDescriptionValue(description))
      return true;
    if (showUnitPicker && !selectedUnit) return true;
    if (!isAuthenticated) {
      if (validateEmailValue(email)) return true;
      if (params.authFlow !== 'true') {
        const phoneError = validatePhoneValue(phone);
        if (phoneError) return true;
      }
    }
    return false;
  }, [
    isSubmitting,
    categoriesLoading,
    selectedCategory,
    description,
    isAuthenticated,
    showUnitPicker,
    selectedUnit,
    email,
    phone,
  ]);

  const handleSubmit = async () => {
    if (!validate() || !selectedCategory) return;
    setIsSubmitting(true);
    try {
      const resolvedCountryCode = (code: string | undefined) => {
        if (!code) return "";
        return code.startsWith("+") ? code.slice(1) : code;
      };
      const parentSalesOrderId = (selectedProperty as any)?.salesOrderId;

      const payload = isAuthenticated
        ? {
            requestTypeId: selectedCategory.id,
            parentId: parentSalesOrderId || "",
            countryCode: resolvedCountryCode(
              user?.primary_country_code || selectedCountry.code,
            ),
            phoneNumber: user?.primary_phone || phone || "",
            email: user?.primary_email || email || "",
            description: description.trim(),
            subject: selectedCategory.label,
            category: selectedCategory.label,
          }
        : {
            requestTypeId: selectedCategory.id,
            parentId: "",
            countryCode: resolvedCountryCode(selectedCountry.code),
            phoneNumber: phone || "",
            email: email || "",
            description: description.trim(),
            subject: selectedCategory.label,
          };
      logger.info(
        "ServiceRequestCreateScreen",
        "Submitting service request",
        payload,
      );
      trackDynatraceEvent({
        p_screen: "serviceRequest",
        p_actionName: "Submit Service Request",
        p_sectionName: selectedCategory.label,
        requestTypeId: selectedCategory.id,
        eventType: "action",
      });
      if (isAdhaJourney) {
        trackCatalogEvent({
          screen: DT_SCREEN.ALMETLAA_SUPPORT,
          actionName: ALMETLAA_ACTION.SUPPORT.SUBMIT_CLICK,
          eventType: "action",
          properties: { requestTypeId: selectedCategory.id },
        });
      }
      const result = await (ServiceRequestService as any).createServiceRequest(
        payload,
      );
      logger.info(
        "ServiceRequestCreateScreen",
        "Service request created",
        result,
      );
      trackDynatraceEvent({
        p_screen: "serviceRequest",
        p_actionName: "Service Request Submitted",
        p_sectionName: selectedCategory.label,
        requestTypeId: selectedCategory.id,
        eventType: "action",
      });
      if (isAdhaJourney) {
        trackCatalogEvent({
          screen: DT_SCREEN.ALMETLAA_SUPPORT,
          actionName: ALMETLAA_ACTION.SUPPORT.SUBMIT_SUCCESS,
          eventType: "action",
          properties: { requestTypeId: selectedCategory.id },
        });
      }
      await queryClient.invalidateQueries({ queryKey: ["service-requests"] });
      if (Platform.OS === "web") {
        setServiceRequestSuccessVisible(true);
        return;
      }
      router.replace("/service-request-success" as any);
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.data?.message ||
        error?.message;

      logger.error("ServiceRequestCreateScreen", "Submit failed", {
        error: error?.message || error,
      });
      trackDynatraceEvent({
        p_screen: "serviceRequest",
        p_actionName: "Submit Service Request Failed",
        p_sectionName: selectedCategory?.label,
        requestTypeId: selectedCategory?.id,
        p_failedMessage: error?.message || String(error),
        eventType: "error",
      });
      if (isAdhaJourney) {
        trackCatalogEvent({
          screen: DT_SCREEN.ALMETLAA_SUPPORT,
          actionName: ALMETLAA_ACTION.SUPPORT.SUBMIT_ERROR,
          eventType: "error",
          properties: {
            requestTypeId: selectedCategory?.id,
            p_failedMessage: error?.message || String(error),
          },
        });
      }
      if (typeof apiMessage === "string" && apiMessage.trim().length > 0) {
        setToastMessage(apiMessage.trim());
        setToastVisible(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDesktopWeb = Platform.OS === "web" && !isSmallScreen();

  const openUnitPicker = useCallback(() => {
    trackCatalogEvent({
      screen: DT_SCREEN.SERVICE_REQUEST_CREATE,
      actionName: 'service_request_unit_picker_open',
      eventType: 'action',
    });
    setTouched((prev) => ({ ...prev, unit: true }));
    measureOptionPickerAnchor(
      unitPickerTriggerRef,
      (anchor) => {
        setUnitPickerAnchor(anchor);
        setUnitPickerVisible(true);
      },
      () => {
        setUnitPickerAnchor(null);
        setUnitPickerVisible(true);
      },
    );
  }, []);

  const closeUnitPicker = useCallback(() => {
    setUnitPickerVisible(false);
    setUnitPickerAnchor(null);
  }, []);

  const openCategoryPicker = useCallback(() => {
    setTouched((prev) => ({ ...prev, category: true }));
    measureOptionPickerAnchor(
      categoryPickerTriggerRef,
      (anchor) => {
        setCategoryPickerAnchor(anchor);
        setCategoryPickerVisible(true);
      },
      () => {
        setCategoryPickerAnchor(null);
        setCategoryPickerVisible(true);
      },
    );
  }, []);

  const closeCategoryPicker = useCallback(() => {
    setCategoryPickerVisible(false);
    setCategoryPickerAnchor(null);
  }, []);

  const handleServiceRequestSuccessOk = useCallback(() => {
    setServiceRequestSuccessVisible(false);
    if (isAuthenticated) {
      router.replace("/service-request" as any);
    } else {
      router.replace("/welcome" as any);
    }
  }, [isAuthenticated, router]);

  return (
    <View
      style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}
    >
      <LogoHeader hideLogo showLanguageSwitch={false} />
      <KeyboardAvoidingView
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        keyboardVerticalOffset={0}
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[
            styles.content,
            {
              paddingHorizontal: isDesktopWeb ? 24 : 16,
              paddingTop: Platform.OS === "web" ? 8 : 32,
            },
          ]}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.contentWrapper}>
          <View style={styles.header}>
            <Typography
              variant={Platform.OS === "web" ? "largeTitle" : "title1"}
              color="primary"
              style={{
                // Web: keep RTL alignment; Mobile: always left-align (including Arabic)
                textAlign:
                  Platform.OS === "web" && isRTL ? "right" : "left",
              }}
            >
              {supportLabel("support.createTitle")}
            </Typography>
            <Typography
              variant={Platform.OS === "web" ? "subhead" : "body"}
              color={Platform.OS === "web" ? "primary" : "secondary"}
              style={{
                textAlign:
                  Platform.OS === "web" && isRTL ? "right" : "left",
                ...(Platform.OS !== "web" && {
                  fontSize: 16,
                  lineHeight: 16,
                }),
              }}
            >
              {supportLabel("support.createSubtitle").replace(
                "{{name}}",
                friendlyName || supportLabel("common.welcome"),
              )}
            </Typography>
          </View>

          <View style={styles.form}>
            {showUnitPicker && (
              <View style={styles.field}>
                <FormPicker
                  ref={unitPickerTriggerRef}
                  label={supportLabel("support.selectUnitLabel")}
                  placeholder={supportLabel("support.selectUnitPlaceholder")}
                  selectedOption={selectedUnit || null}
                  isMenuOpen={unitPickerVisible}
                  menuOpensUpward={unitPickerAnchor?.opensUpward === true}
                  onPress={openUnitPicker}
                  {...(errors.unit && { error: errors.unit })}
                  required
                  {...(Platform.OS === "web" && {
                    labelVariant: "headline" as const,
                    labelColor: "primary" as const,
                    emptyColor: "tertiary" as const,
                  })}
                />
              </View>
            )}

            {!isAuthenticated && (
              <>
                <View style={styles.field}>
                  <Typography
                    variant="caption"
                    color="tertiary"
                    style={[
                      styles.label,
                      {
                        // Web: keep RTL alignment; Mobile: always left-align (including Arabic)
                        textAlign:
                          Platform.OS === "web" && isRTL ? "right" : "left",
                      },
                    ]}
                  >
                    {supportLabel("support.emailLabel")} *
                  </Typography>
                  <TextInput
                    value={email}
                    placeholder={supportLabel("support.emailPlaceholder")}
                    onChangeText={(text) => {
                      setEmail(text);
                      if (touched.email) {
                        setErrors((prev) => ({
                          ...prev,
                          email: validateEmailValue(text),
                        }));
                      }
                    }}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoCorrect={false}
                    autoComplete="email"
                    textContentType="emailAddress"
                    onBlur={() => {
                      setTouched((prev) => ({ ...prev, email: true }));
                      setErrors((prev) => ({
                        ...prev,
                        email: validateEmailValue(email),
                      }));
                    }}
                    error={touched.email ? errors.email || "" : ""}
                  />
                </View>
                {params.authFlow !== 'true' && (
                  <PhoneInputRow
                    label={supportLabel("support.phoneLabel")}
                  selectedCountry={selectedCountry}
                  phoneNumber={phone}
                  onCountrySelect={(c: Country) => setSelectedCountry(c)}
                  onPhoneChange={(text: string) => {
                    setPhone(text);
                    if (touched.phone) {
                      setErrors((prev) => ({
                        ...prev,
                        phone: validatePhoneValue(text),
                      }));
                    }
                  }}
                  onPhoneBlur={() => {
                    setTouched((prev) => ({ ...prev, phone: true }));
                    setErrors((prev) => ({
                      ...prev,
                      phone: validatePhoneValue(phone),
                    }));
                  }}
                  error={touched.phone ? errors.phone || "" : ""}
                />
                )}
              </>
            )}

            <View style={styles.field}>
              {categoriesLoading ? (
                <View style={styles.categorySkeleton}>
                  <Skeleton
                    height={12}
                    borderRadius={6}
                    style={{ width: "40%" }}
                  />
                  <Skeleton height={48} borderRadius={8} />
                </View>
              ) : (
                <FormPicker
                  ref={categoryPickerTriggerRef}
                  label={supportLabel("support.selectCategoryLabel")}
                  placeholder={supportLabel("support.selectCategoryPlaceholder")}
                  selectedOption={selectedCategory || null}
                  isMenuOpen={categoryPickerVisible}
                  menuOpensUpward={categoryPickerAnchor?.opensUpward === true}
                  onPress={openCategoryPicker}
                  {...(errors.category && { error: errors.category })}
                  required
                  {...(Platform.OS === "web" && {
                    labelVariant: "headline" as const,
                    labelColor: "primary" as const,
                    emptyColor: "tertiary" as const,
                  })}
                />
              )}
            </View>

            <View style={styles.field}>
              {/* Web: external Typography label. Native: TextInput built-in label. */}
              {Platform.OS === "web" ? (
                <Typography
                  variant="headline"
                  color="primary"
                  style={[
                    styles.label,
                    {
                      textAlign: isRTL ? "right" : "left",
                    },
                  ]}
                >
                  {supportLabel("support.describeIssueLabel")} *
                </Typography>
              ) : null}
              <TextInput
                value={description}
                placeholder={supportLabel("support.describeIssuePlaceholder")}
                {...(Platform.OS !== "web"
                  ? {
                      label: t("support.describeIssueLabel" as any),
                      required: true,
                    }
                  : {})}
                onChangeText={(text) => {
                  setDescription(text);
                  if (touched.description) {
                    setErrors((prev) => ({
                      ...prev,
                      description: validateDescriptionValue(text),
                    }));
                  }
                }}
                multiline
                numberOfLines={4}
                textAlignVertical="top"
                inputStyle={[
                  styles.textAreaInput,
                  Platform.OS === "web" && styles.descriptionInputWeb,
                ]}
                {...(Platform.OS === "web" && {
                  placeholderTextColor: colors.fontTertiary,
                })}
                onBlur={() => {
                  setTouched((prev) => ({ ...prev, description: true }));
                  setErrors((prev) => ({
                    ...prev,
                    description: validateDescriptionValue(description),
                  }));
                }}
                error={touched.description ? errors.description || "" : ""}
              />
            </View>
          </View>

          <View
            style={[
              styles.submitContainer,
              { paddingBottom: Math.max(insets.bottom, 24) },
            ]}
          >
            <Button
              title={supportLabel("support.submitRequestCta")}
              onPress={handleSubmit}
              variant="primary"
              fullWidth
              disabled={isSubmitDisabled}
              style={Platform.OS !== "web" ? styles.submitButton : undefined}
              textStyle={
                Platform.OS !== "web"
                  ? [styles.submitButtonText, { color: colors.white }]
                  : undefined
              }
            />
            {/* Callback CTA temporarily disabled until flow is wired */}
            {/* <Button
                    title={supportLabel('REQUEST_CALLBACK_CTA', 'support.requestCallbackCta')}
                    onPress={() => {
                      logger.info("ServiceRequestCreateScreen", "Request callback pressed");
                    }}
                    variant="text"
                    fullWidth
                    style={styles.callbackButton}
                    disabled={isSubmitting}
                /> */}
          </View>
        </View>
      </ScrollView>
      </KeyboardAvoidingView>

      {showUnitPicker && (
        <OptionPickerModal
          visible={unitPickerVisible}
          title={supportLabel("support.selectUnitLabel")}
          options={unitOptions}
          selectedKey={selectedUnit?.id ?? null}
          anchor={unitPickerAnchor}
          onSelect={(opt) => {
            setSelectedUnit(opt);
            setErrors((prev) => {
              const next = { ...prev };
              delete next.unit;
              return next;
            });
            closeUnitPicker();
          }}
          onClose={closeUnitPicker}
        />
      )}

      <OptionPickerModal
        visible={categoryPickerVisible}
        title={supportLabel("support.selectCategoryLabel")}
        options={categoryOptions}
        selectedKey={
          selectedCategory ? selectedCategory.key || selectedCategory.id : null
        }
        loading={categoriesLoading}
        anchor={categoryPickerAnchor}
        onSelect={(opt) => {
          setSelectedCategory(opt);
          setErrors((prev) => {
            const next = { ...prev };
            delete next.category;
            return next;
          });
          trackDynatraceEvent({
            p_screen: "serviceRequest",
            p_actionName: "Select Service Request Category",
            p_sectionName: opt.label,
            requestTypeId: opt.id,
            eventType: "action",
          });
          closeCategoryPicker();
        }}
        onClose={closeCategoryPicker}
      />

      <FullScreenLoader visible={isSubmitting} />
      <Toast
        visible={toastVisible}
        message={toastMessage}
        duration={4000}
        onHide={() => setToastVisible(false)}
      />
      {Platform.OS === "web" && (
        <ServiceRequestSuccessDialog
          visible={serviceRequestSuccessVisible}
          onOk={handleServiceRequestSuccessOk}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    paddingBottom: 32,
    alignItems: "center",
  },
  contentWrapper: {
    width: "100%",
    maxWidth: Platform.OS === "web" ? 600 : 900,
    ...(Platform.OS === "web"
      ? {
          flex: 1,
          gap: 32, // web spacing (native already had gap below)
        }
      : {
          gap: 32,
        }),
  },
  header: {
    gap: 12,
  },
  form: {
    gap: 16,
  },
  field: {
    gap: 8,
  },
  categorySkeleton: {
    gap: 8,
  },
  picker: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    alignItems: "center",
    gap: 10,
    minHeight: 48,
  },
  phoneRow: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
  },
  countryInput: {
    flex: 0.3,
  },
  phoneInput: {
    flex: 0.7,
  },
  label: {
    paddingHorizontal: Platform.OS === 'web' ? 8 : 0,
  },
  textAreaContainer: {
    minHeight: Platform.OS === 'web' ? 140 : 108,
  },
  textAreaInput: {
    minHeight: Platform.OS === 'web' ? 140 : 108,
  },
  descriptionInputWeb: {
    fontSize: 16,
    fontFamily: "Matter-Regular",
    fontWeight: "400",
    lineHeight: 16,
  },
submitContainer: {
  paddingTop: Platform.OS === 'web' ? 8 : 12,
  ...(Platform.OS !== 'web' && {
    marginTop: -20,
  }),
  gap: 8,
},
  submitButton: {
    borderRadius: 8,
    paddingVertical: 12,
    height: 49,
  },
  submitButtonText: {
    lineHeight: 16,
    fontWeight: "600",
  },
  callbackButton: {
    marginTop: 0,
  },
});
